/**
 * Privacy Policy Page
 * Route: /privacy
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import SEO from '@/components/common/SEO';

const PrivacyPolicy: React.FC = () => {
  const sections = [
    {
      title: '1. Introduction',
      content: `MemAds ("we", "us", or "our") is committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our mobile application. Please read this policy carefully. If you disagree with the terms of this Privacy Policy, please discontinue use of our Platform.`,
    },
    {
      title: '2. Information We Collect',
      content: `We collect information you provide directly to us, including: (a) Account Information — your name, email address, phone number, and password when you register; (b) Profile Information — your profile picture, city, and other optional details; (c) Listing Information — descriptions, photos, prices, and other details you provide when posting ads; (d) Communication Data — messages you send to other users through our platform; (e) Payment Information — billing details (processed securely, not stored by us); (f) Usage Data — your interactions with the Platform, IP address, browser type, pages visited, and timestamps.`,
    },
    {
      title: '3. How We Use Your Information',
      content: `We use the information we collect to: operate, maintain, and improve the Platform; process transactions and send related information; send administrative communications such as confirmations and security alerts; respond to your comments and questions; send promotional communications (you may opt-out at any time); monitor and analyse usage patterns; detect, prevent, and address technical issues and fraudulent activity; comply with legal obligations.`,
    },
    {
      title: '4. Sharing Your Information',
      content: `We do not sell or rent your personal information to third parties. We may share your information with: (a) Other Users — your public profile and listing details are visible to other users of the Platform; (b) Service Providers — trusted vendors who assist in operating the Platform (e.g., cloud hosting, payment processors, email services) under strict confidentiality agreements; (c) Legal Requirements — when required by law, regulation, or court order; (d) Business Transfers — in the event of a merger, acquisition, or sale of assets, your information may be transferred.`,
    },
    {
      title: '5. Data Storage and Security',
      content: `Your data is stored on secure servers hosted in India. We implement industry-standard security measures including HTTPS encryption, secure password hashing (bcrypt), and regular security audits to protect your personal information. However, no method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security. You are responsible for maintaining the confidentiality of your account credentials.`,
    },
    {
      title: '6. Cookies and Tracking',
      content: `We use cookies and similar tracking technologies to track activity on our Platform and hold certain information. Cookies are small data files stored on your device. We use session cookies (which expire when you close your browser) and persistent cookies (which remain until deleted). You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. For full details, please see our Cookie Policy.`,
    },
    {
      title: '7. Your Privacy Rights',
      content: `You have the following rights regarding your personal data: (a) Access — request a copy of the personal data we hold about you; (b) Correction — request correction of inaccurate or incomplete data; (c) Deletion — request deletion of your personal data, subject to legal obligations; (d) Portability — request transfer of your data in a machine-readable format; (e) Objection — object to processing of your data for direct marketing; (f) Withdrawal of Consent — withdraw consent at any time where we rely on consent. To exercise these rights, contact us at privacy@memads.in.`,
    },
    {
      title: '8. Children\'s Privacy',
      content: `MemAds is not intended for use by children under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that we have inadvertently collected personal information from a child under 18, we will take steps to delete such information promptly.`,
    },
    {
      title: '9. Third-Party Links',
      content: `Our Platform may contain links to third-party websites and services. We are not responsible for the privacy practices of those websites. We encourage you to review the privacy policy of every website you visit.`,
    },
    {
      title: '10. Changes to This Policy',
      content: `We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically. Your continued use of the Platform after changes are posted constitutes your acceptance of the updated policy.`,
    },
    {
      title: '11. Contact Us',
      content: `If you have questions or concerns about this Privacy Policy or our data practices, please contact our Privacy Team at: privacy@memads.in. We aim to respond to all privacy-related inquiries within 30 days.`,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Privacy Policy"
        description="Learn how MemAds collects, stores, and protects your personal information. Read our data usage policy."
        canonical="/privacy"
      />
      {/* Page Header */}
      <div className="border-b border-border bg-secondary/30">
        <div className="mx-auto max-w-4xl px-4 py-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary text-primary-foreground">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground">Privacy Policy</h1>
              <p className="text-sm text-muted-foreground">Last updated: June 2025</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="rounded-2xl border border-border bg-card p-8 shadow-sm">
          <p className="text-muted-foreground leading-relaxed mb-8">
            Your privacy matters to us. This policy explains how <strong className="text-foreground">MemAds</strong> handles your personal data, your rights, and how we keep your information safe.
          </p>

          <div className="space-y-8">
            {sections.map((section) => (
              <div key={section.title} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h2 className="font-heading text-lg font-semibold text-foreground mb-3">{section.title}</h2>
                <p className="text-muted-foreground leading-relaxed text-sm">{section.content}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-8 flex flex-wrap gap-3 text-sm">
          <span className="text-muted-foreground">Related:</span>
          <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
          <span className="text-border">·</span>
          <Link to="/cookies" className="text-primary hover:underline">Cookie Policy</Link>
          <span className="text-border">·</span>
          <a href="mailto:privacy@memads.in" className="text-primary hover:underline">Contact Privacy Team</a>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
